import { Currency } from '@seapoint/server/enums';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { ChartConfig, ChartContainer, ChartTooltip } from '../../../../design-system/chart/chart';
import { cn } from '../../../../utils/cn';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';

export type MoneyMovementDataPoint = {
  period: string; // Display label (e.g., "19 May", "24 May")
  moneyIn: number; // Positive amount for money coming in
  moneyOut: number; // Positive amount for money going out (will be displayed as negative)
  date: string; // ISO date string for this period
};

type MoneyMovementChartProps = {
  height?: number;
  data: MoneyMovementDataPoint[];
  baseCurrency: Currency;
  className?: string;
};

const chartConfig = {
  moneyIn: {
    label: 'Money In',
    color: 'rgb(143, 206, 128)',
  },
  moneyOut: {
    label: 'Money Out',
    color: 'rgb(255, 119, 119)',
  },
} satisfies ChartConfig;

export const MoneyMovementChart = ({ height = 300, data, baseCurrency, className }: MoneyMovementChartProps) => {
  // Keep all values positive - red color will indicate outgoing amounts
  const chartData = data.map((point) => ({
    ...point,
    moneyOut: Math.abs(point.moneyOut), // Ensure money out is positive for upward bars
  }));

  return (
    <ChartContainer config={chartConfig} className={cn(`h-[${height}px]`, 'w-full', className)}>
      <BarChart accessibilityLayer data={chartData} barCategoryGap={0} barGap={0}>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="period"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tick={{ fontSize: 12 }}
          interval={'equidistantPreserveStart'}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickCount={5}
          tickFormatter={(value) => {
            const numValue = Math.abs(value as number);
            // For chart Y-axis, format large numbers in a more compact way
            if (numValue >= 1000000) {
              return `${baseCurrency === 'GBP' ? '£' : '€'}${(numValue / 1000000).toFixed(1)}M`;
            } else if (numValue >= 1000) {
              return `${baseCurrency === 'GBP' ? '£' : '€'}${(numValue / 1000).toFixed(0)}K`;
            } else {
              return getAmountWithCurrency({
                amount: numValue,
                currency: baseCurrency,
                isAccountBalance: true,
              });
            }
          }}
        />
        <ChartTooltip
          cursor={false}
          content={({ active, payload, label }) => {
            if (active && payload && payload.length > 0) {
              const originalDataPoint = payload[0]?.payload as MoneyMovementDataPoint | undefined;
              if (!originalDataPoint) return null;

              const moneyIn = originalDataPoint.moneyIn;
              const moneyOut = originalDataPoint.moneyOut;

              const formattedMoneyIn = getAmountWithCurrency({
                amount: moneyIn,
                currency: baseCurrency,
                isAccountBalance: true,
              });

              const formattedMoneyOut = getAmountWithCurrency({
                amount: moneyOut,
                currency: baseCurrency,
                isAccountBalance: true,
              });

              return (
                <div className="rounded-lg border bg-white p-2 shadow-md">
                  <div className="grid gap-2">
                    <div className="font-medium">{label}</div>
                    <div className="grid gap-1 text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: chartConfig.moneyIn.color }} />
                          <span className="text-muted-foreground">Money In:</span>
                        </div>
                        <span className="font-medium text-seapoint-green">{formattedMoneyIn}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: chartConfig.moneyOut.color }} />
                          <span className="text-muted-foreground">Money Out:</span>
                        </div>
                        <span className="font-medium text-seapoint-red">{formattedMoneyOut}</span>
                      </div>
                      <div className="border-t pt-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-muted-foreground font-medium">Net:</span>
                          <span
                            className={cn(
                              'font-medium',
                              moneyIn - moneyOut >= 0 ? 'text-seapoint-green' : 'text-seapoint-red',
                            )}
                          >
                            {getAmountWithCurrency({
                              amount: moneyIn - moneyOut,
                              currency: baseCurrency,
                              isAccountBalance: true,
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          }}
        />
        <Bar dataKey="moneyIn" fill={chartConfig.moneyIn.color} radius={[2, 2, 0, 0]} barSize={24} maxBarSize={24} />
        <Bar dataKey="moneyOut" fill={chartConfig.moneyOut.color} radius={[2, 2, 0, 0]} barSize={24} maxBarSize={24} />
      </BarChart>
    </ChartContainer>
  );
};
